import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "../authConfig";
import {
  logoutUser,
  resolveCurrentAccount,
  shouldForceRelogin,
  clearForceReloginFlag,
} from "../utils/msalLogout";
import {
  markAuthenticated,
  wasAuthenticated,
  clearAuthenticatedMarker,
  hasMsalTokenCacheInLocalStorage,
  shouldPromptLoginAfterSessionLoss,
} from "../utils/authSession";

async function redirectToLogin(instance, { promptLogin }) {
  if (promptLogin) {
    await instance.clearCache();
  }

  await instance.loginRedirect({
    ...loginRequest,
    ...(promptLogin ? { prompt: "login" } : {}),
  });
}

const AuthenticationCheck = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [checkComplete, setCheckComplete] = useState(false);

  useEffect(() => {
    const validateUser = async () => {
      if (inProgress !== InteractionStatus.None) {
        return;
      }

      const hadAppSession = wasAuthenticated();
      const msalCachePresent = hasMsalTokenCacheInLocalStorage();
      const hasAccounts = accounts && accounts.length > 0;

      setCheckComplete(false);

      // localStorage cleared while the app still considers the user signed in.
      if (hadAppSession && !msalCachePresent) {
        clearAuthenticatedMarker();
        clearForceReloginFlag();

        try {
          await redirectToLogin(instance, { promptLogin: true });
        } catch (error) {
          if (error.errorCode !== "interaction_in_progress") {
            console.error("Login redirect failed:", error);
          }
        }
        return;
      }

      if (!hasAccounts) {
        const forceRelogin = shouldForceRelogin();
        const promptLogin = shouldPromptLoginAfterSessionLoss({
          forceRelogin,
          hadAppSession,
          msalCachePresent,
          hasAccounts: false,
        });

        clearForceReloginFlag();
        if (promptLogin) {
          clearAuthenticatedMarker();
        }

        try {
          await redirectToLogin(instance, { promptLogin });
        } catch (error) {
          if (error.errorCode !== "interaction_in_progress") {
            console.error("Login redirect failed:", error);
          }
        }
        return;
      }

      const account = resolveCurrentAccount(instance, accounts);
      if (!account) {
        return;
      }

      const tokenAud = account?.idTokenClaims?.aud;
      const audiences = Array.isArray(tokenAud) ? tokenAud : [tokenAud];
      const isValidTenant = account?.tenantId === process.env.REACT_APP_TENANT_ID;
      const isValidClientId = audiences.includes(process.env.REACT_APP_CLIENT_ID);

      if (!isValidTenant || !isValidClientId) {
        try {
          await logoutUser(instance, account);
        } catch (error) {
          console.error("Logout redirect failed:", error);
        }
        return;
      }

      instance.setActiveAccount(account);
      markAuthenticated(account.homeAccountId);
      setCheckComplete(true);
    };

    validateUser();
  }, [instance, accounts, inProgress]);

  if (!checkComplete) {
    return <div>Validating user...</div>;
  }

  return children;
};

export default AuthenticationCheck;
